import { useMutation } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';

import type { AuthCustomer } from '@getjust/api-schema';

import { useCustomRouter } from '$hooks/client';
import { useUpdateEmail } from '$hooks/mutations';
import { useFormMessageAtom, useLoginAtom, useTemporaryCartKeyAtom } from '$hooks/state';
import { MUTATION_LOGIN_KEY } from '$src/constants';
import { useCart } from '$src/hooks/queries';
import { useShowChallengeAtom } from '$src/hooks/state/useShowChallengeAtom';
import { authAPI } from '$src/http';

export const useLogin = () => {
  const { mutateAsync: updateEmail } = useUpdateEmail();
  const { onLoginChange } = useLoginAtom();
  const router = useCustomRouter();
  const { showLoginChallenge } = useShowChallengeAtom();
  const { formMessage } = useFormMessageAtom();
  const { data: cart } = useCart();
  const { onCartKeyChange } = useTemporaryCartKeyAtom();

  return useMutation({
    mutationFn: (email: string) => {
      const cartKey = uuidv4();
      onCartKeyChange(cartKey);

      const payload: AuthCustomer.AuthLoginRequestBody = {
        email,
        clientId: formMessage?.shopId ?? 'web',
        cartkey: cartKey,
        ...(cart?.id ? { cart: cart.id } : {}),
      };

      return authAPI.post<AuthCustomer.AuthLoginResponseBody>('/login', payload);
    },
    mutationKey: [MUTATION_LOGIN_KEY],
    onError: async (error: any, email: string) => {
      if (!router.pathname.includes('/unauthenticated')) {
        router.push('/unauthenticated');
      }

      if (router.pathname.includes('/unauthenticated')) {
        await updateEmail({
          email,
        });
      }
    },
    onSuccess: ({ data }) => {
      onLoginChange(data);
      showLoginChallenge();
    },
  });
};
